import { Tooltip, TooltipProps } from "@mui/material";
import { format, startOfDay, subDays } from "date-fns";
import { FC } from "react";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { isDate, toDefaultDate, toLocalAndUtcDates } from "../../lib/Util";
import { InlineBlock } from "../ui/common";
import "./index.css";

interface DateRangeProps {
  start: Date;
  end: Date;
}

// Helper class to manage a range of dates.
class DateRange {
  start: Date;
  end: Date;

  constructor(start: Date, end: Date) {
    this.start = startOfDay(start);
    this.end = startOfDay(end);
  }

  static fromString(start: string, end: string): DateRange | null {
    //if the date is splitted by "-" change by "/" so Date() can parse it
    const startParsed = start.replace(/-/g, "/");
    const endParsed = end.replace(/-/g, "/");
    const endDate = startOfDay(new Date(endParsed));
    const startDate = startOfDay(new Date(startParsed));
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return null;
    }
    return new DateRange(startDate, endDate);
  }

  static fromDays(days: number, relativeDate?: Date) {
    const endDate = relativeDate ? new Date(relativeDate) : new Date();
    const startDate = subDays(endDate, days);
    return new DateRange(startDate, endDate);
  }

  static fromDateRange(dateRange: DateRangeProps) {
    return new DateRange(dateRange.start, dateRange.end);
  }

  toDateRange() {
    return {
      start: this.start,
      end: this.end,
    };
  }

  toSearchRange() {
    return {
      start: format(this.start, "yyyy-MM-dd"),
      end: format(this.end, "yyyy-MM-dd"),
    };
  }
}

interface HumanDateProps {
  date?: string | null;
  placement?: TooltipProps["placement"];
  className?: string;
}

// Human friendly default format of dates in our UI.
const HumanDate: FC<HumanDateProps> = ({ date, placement = "left" }) => {
  if (date && isDate(date)) {
    const { browser, utc } = toLocalAndUtcDates(date);

    return (
      <Tooltip
        id="tooltip"
        arrow
        placement={placement}
        title={
          <>
            {browser}
            <br />
            {utc}
          </>
        }
      >
        <InlineBlock>
          <span className="human-date" data-testid="tooltip-container-human-date">
            {toDefaultDate(date)}
          </span>
        </InlineBlock>
      </Tooltip>
    );
  } else {
    return null;
  }
};

export { DateRange, HumanDate };
export type { DateRangeProps };
